import { Controller } from "@hotwired/stimulus";

// One configuration, one result. A normal day pays only the hours actually
// worked, plus the night percent on 22:00–06:00. A vacation pays the jornada
// even when fewer hours are worked. A festivo pays the jornada and, on top,
// the hours worked at the festivo percent (140 by default). Shows use the
// session split. Concerts use ticket income minus fixed and variable costs.
export default class extends Controller {
  static targets = ["payload"];
  static values = { dayUrl: String, saveUrl: String, saveMethod: String, listUrl: String };

  connect() {
    this.requestId = 0;
    this.dirty = false;
    this.onEdit = this.onEdit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.element.addEventListener("input", this.onEdit);
    this.element.addEventListener("change", this.onEdit);
    this.element.addEventListener("click", this.onClick);
    this.state = JSON.parse(this.payloadTarget.textContent);
    this.renderAll();
  }

  disconnect() {
    this.element.removeEventListener("input", this.onEdit);
    this.element.removeEventListener("change", this.onEdit);
    this.element.removeEventListener("click", this.onClick);
  }

  changeDate(event) {
    this.loadDate(event.target.value);
  }

  async loadDate(date) {
    if (!date) return;
    const token = ++this.requestId;
    try {
      const response = await fetch(`${this.dayUrlValue}?date=${date}`, {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) throw new Error(response.status);
      const payload = await response.json();
      if (token !== this.requestId) return;
      this.state = payload;
      this.renderAll();
      this.setDirty(true);
    } catch (error) {
      if (token !== this.requestId) return;
      this.qs("#weekdayLabel").textContent = "No se ha podido cargar esta fecha.";
    }
  }

  onEdit(event) {
    const el = event.target;
    if (!el.dataset.k) {
      if (el.dataset.toggleComing != null) this.toggleComing(Number(el.dataset.toggleComing), el.checked);
      if (el.dataset.include) this.toggleInclude(el.dataset.include, Number(el.dataset.index), el.checked);
      if (el.dataset.toggleComing != null || el.dataset.include) this.setDirty(true);
      return;
    }
    let value = el.value;
    if (el.type === "number") value = value === "" ? null : Number(value);
    this.set(el.dataset.k, value);
    this.setDirty(true);
    this.compute();
  }

  onClick(event) {
    const el = event.target.closest("[data-add-employee], [data-add-show], [data-add-concert], [data-add-slot], [data-remove-slot], [data-remove-employee], [data-remove-show], [data-remove-concert]");
    if (!el) return;
    event.preventDefault();
    this.setDirty(true);
    if (el.hasAttribute("data-add-employee")) this.addEmployee();
    else if (el.hasAttribute("data-add-show")) this.addShow();
    else if (el.hasAttribute("data-add-concert")) this.addConcert();
    else if (el.dataset.addSlot != null) this.addSlot(Number(el.dataset.addSlot));
    else if (el.dataset.removeSlot != null) this.removeSlot(Number(el.dataset.removeSlot), Number(el.dataset.slot));
    else if (el.dataset.removeEmployee != null) this.removeEmployee(Number(el.dataset.removeEmployee));
    else if (el.dataset.removeShow != null) this.removeRow("shows", Number(el.dataset.removeShow));
    else if (el.dataset.removeConcert != null) this.removeRow("concerts", Number(el.dataset.removeConcert));
  }

  renderAll() {
    const dateInput = this.qs("#dateInput");
    if (dateInput) dateInput.value = this.state.date;
    const date = new Date(`${this.state.date}T12:00:00`);
    this.qs("#weekdayLabel").textContent = date.toLocaleDateString("es-ES", {
      weekday: "long", day: "numeric", month: "long", year: "numeric"
    });
    this.qs("#holidaySlot").innerHTML = this.state.holiday
      ? `<span class="holiday-banner"><i class="fas fa-calendar-alt"></i> Festivo: <strong>${this.esc(this.state.holiday)}</strong> · se paga la jornada y, además, las horas trabajadas al ${this.esc(String(this.state.params.holidayPct))} %</span>`
      : "";
    this.element.querySelectorAll("[data-k^='params.'], [data-k^='bar.']").forEach((el) => {
      const value = this.get(el.dataset.k);
      el.value = value == null ? "" : value;
    });
    this.qs("#barSource").textContent = this.state.bar.source ? `Precargado: ${this.state.bar.source}` : "";
    this.renderReference();
    this.renderStaff();
    this.renderShows();
    this.renderConcerts();
    this.compute();
  }

  renderReference() {
    const reference = this.state.reference || {};
    this.qs("#referenceScope").textContent = reference.label || "";
    const card = (label, stats, color) => {
      if (!stats) return "";
      return `<div class="col-3"><div class="kpi ${color}"><div class="lbl">${label}</div><div class="val">${this.num1(stats.tickets)} <span class="fs-6 text-secondary">entradas</span></div><div class="sub">${Math.round(stats.fill * 100)} % de llenado</div>${this.detailLines(stats.lines, (value) => this.entradasCount(value))}</div></div>`;
    };
    const bar = reference.bar;
    const html = [
      card("Espectáculos 20:00", reference.theatre20, "b-blue"),
      card("Espectáculos 21:00", reference.theatre21, "b-blue"),
      card("Espectáculos 22:30", reference.theatre2230, "b-blue"),
      card("Conciertos", reference.concerts, "b-purple"),
      bar ? `<div class="col-3"><div class="kpi b-green"><div class="lbl">Bar — ingresos medios sin IVA</div><div class="val">${this.eur(bar.avg)}</div>${this.detailLines(bar.lines, (value) => this.eur(value))}</div></div>` : ""
    ].join("");
    const cards = this.qs("#referenceCards");
    cards.innerHTML = html;
    cards.parentElement.hidden = html === "";
  }

  detailLines(lines, format) {
    if (!lines || !lines.length) return "";
    return `<ul class="kpi-lines">${lines.map((line) => {
      const date = line.url
        ? `<a href="${this.esc(line.url)}" target="_blank" rel="noopener">${this.esc(line.label)}</a>`
        : this.esc(line.label);
      const name = line.name ? `<span class="kpi-event">${this.esc(line.name)}</span>` : "";
      return `<li><span class="kpi-when">${date}${name}</span><span>${format(line.value)}</span></li>`;
    }).join("")}</ul>`;
  }

  defaultTickets(time, concert) {
    const reference = this.state.reference || {};
    let stats;
    if (concert) {
      stats = reference.concerts;
    } else {
      const [hours, minutes] = time.split(":").map(Number);
      const total = hours * 60 + minutes;
      if (total >= 22 * 60) stats = reference.theatre2230;
      else if (total >= 21 * 60) stats = reference.theatre21;
      else if (total >= 20 * 60) stats = reference.theatre20;
    }
    return stats && stats.tickets != null ? Math.round(stats.tickets) : 0;
  }

  entradasCount(value) {
    return Number.isInteger(value) ? String(value) : this.num1(value);
  }

  renderStaff() {
    const body = this.qs("#staffBody");
    body.innerHTML = this.state.staff.length
      ? this.state.staff.map((emp, i) => this.staffRow(emp, i)).join("")
      : '<tr><td colspan="5" class="text-secondary text-center py-3">Nadie tiene jornada este día.</td></tr>';
    this.renderAddEmployee();
  }

  staffRow(emp, i) {
    const jornada = emp.jornada.length
      ? `<div class="jornada-box">${emp.jornada.map((slot) => this.slotLabel(slot)).join("<br>")}<br><span class="text-secondary" id="jornadaInfo${i}"></span></div>`
      : `<div class="jornada-box text-secondary">Sin jornada este día<br><span id="jornadaInfo${i}"></span></div>`;
    const real = emp.coming
      ? emp.real.map((slot, k) => `<div class="mb-1 text-nowrap">
          <input type="time" class="form-control form-control-sm slot-input" value="${this.esc(slot[0])}" data-k="staff.${i}.real.${k}.0"> –
          <input type="time" class="form-control form-control-sm slot-input" value="${this.esc(slot[1])}" data-k="staff.${i}.real.${k}.1">
          <a href="#" class="text-danger ms-1" title="Quitar tramo" data-remove-slot="${i}" data-slot="${k}"><i class="fas fa-times-circle"></i></a></div>`).join("")
        + `<a href="#" class="small" data-add-slot="${i}"><i class="fas fa-plus-circle"></i> tramo</a>`
      : '<div class="mb-1 text-nowrap"><input type="time" class="form-control form-control-sm slot-input" disabled> – <input type="time" class="form-control form-control-sm slot-input" disabled></div>';
    return `<tr class="${emp.coming ? "" : "not-coming"} ${emp.added ? "added" : ""}">
      <td class="fw-semibold">${this.esc(emp.name)}<div class="text-secondary fw-normal small">${this.eur(emp.rate)}/h</div>
        ${emp.vacation ? '<span class="badge bg-info text-dark">Vacaciones</span>' : ""}
        ${emp.added ? `<a href="#" class="small text-danger" data-remove-employee="${i}">Quitar</a>` : ""}</td>
      <td>${jornada}</td>
      <td class="text-center"><div class="form-check form-switch d-inline-block"><input class="form-check-input" type="checkbox" ${emp.coming ? "checked" : ""} data-toggle-coming="${i}"></div></td>
      <td class="real-col">${real}<div class="small text-secondary mt-1" id="realInfo${i}"></div></td>
      <td class="text-end"><div class="cost-total" id="costTotal${i}"></div><div class="cost-chips" id="costChips${i}"></div></td></tr>`;
  }

  renderAddEmployee() {
    const present = new Set(this.state.staff.map((emp) => emp.id));
    const options = (this.state.pool || []).filter((person) => !present.has(person.id));
    const row = this.qs("#addEmployeeRow");
    const select = this.qs("#addEmployeeSelect");
    row.classList.toggle("d-none", options.length === 0);
    select.innerHTML = options.map((person) => `<option value="${person.id}">${this.esc(person.name)}</option>`).join("");
  }

  renderShows() {
    this.qs("#showsBody").innerHTML = this.state.shows.length
      ? this.state.shows.map((show, i) => `<tr class="${show.include ? "" : "excluded-row"}">
          <td>${this.txt(`shows.${i}.time`, show.time, "time")}</td>
          <td>${this.nameCell(`shows.${i}.name`, show.name, show.source)}</td>
          <td>${this.num(`shows.${i}.tickets`, show.tickets)}</td>
          <td>${this.num(`shows.${i}.price`, show.price, 0.5)}</td>
          <td>${this.num(`shows.${i}.value`, show.value, 0.5)}</td>
          <td>${this.sel(`shows.${i}.shareType`, show.shareType, [["pct", "% de taquilla"], ["per_ticket", "€ por entrada"], ["fixed", "€ fijo"]])}</td>
          <td>${this.sel(`shows.${i}.recipient`, show.recipient, [["venue", "la sala"], ["artist", "el artista"]])}</td>
          <td>${this.num(`shows.${i}.venueMin`, show.venueMin)}</td>
          <td class="text-center incl">${this.includeSwitch("shows", i, show.include)}</td>
          <td class="amount fw-semibold text-nowrap" id="showVenue${i}"></td>
          <td class="text-center"><a href="#" class="text-danger" title="Quitar" data-remove-show="${i}"><i class="fas fa-times-circle"></i></a></td>
        </tr>`).join("")
      : '<tr><td colspan="11" class="text-secondary text-center py-3">No hay espectáculos este día.</td></tr>';
  }

  renderConcerts() {
    this.qs("#concertsBody").innerHTML = this.state.concerts.length
      ? this.state.concerts.map((concert, i) => `<tr class="${concert.include ? "" : "excluded-row"}">
          <td>${this.txt(`concerts.${i}.time`, concert.time, "time")}</td>
          <td>${this.nameCell(`concerts.${i}.name`, concert.name, concert.source)}</td>
          <td>${this.num(`concerts.${i}.tickets`, concert.tickets)}</td>
          <td>${this.num(`concerts.${i}.price`, concert.price, 0.5)}</td>
          <td>${this.num(`concerts.${i}.fixedCost`, concert.fixedCost)}</td>
          <td>${this.num(`concerts.${i}.variableCost`, concert.variableCost, 0.5)}</td>
          <td class="text-center incl">${this.includeSwitch("concerts", i, concert.include)}</td>
          <td class="amount fw-semibold text-nowrap" id="concertResult${i}"></td>
          <td class="text-center"><a href="#" class="text-danger" title="Quitar" data-remove-concert="${i}"><i class="fas fa-times-circle"></i></a></td>
        </tr>`).join("")
      : '<tr><td colspan="9" class="text-secondary text-center py-3">No hay conciertos este día.</td></tr>';
  }

  compute() {
    const lines = [];
    let total = 0;
    let warning = "";
    const bar = this.state.bar;

    if (bar.revenue != null && bar.marginPct != null) {
      const contribution = bar.revenue * bar.marginPct / 100;
      total += contribution;
      this.qs("#barContribution").textContent = this.eur(contribution);
      lines.push(["Bar", `${this.eur(bar.revenue)} × ${bar.marginPct} %`, contribution]);
    } else {
      this.qs("#barContribution").textContent = "—";
      lines.push(["Bar", bar.revenue == null ? "Faltan ingresos y margen" : "Falta el margen %", null]);
      warning = `<i class="fas fa-exclamation-triangle"></i> Resultado sin el bar: falta ${bar.revenue == null ? "ingresos y margen" : "el margen %"}.`;
    }

    let showsTotal = 0;
    let showsTickets = 0;
    this.state.shows.forEach((show, i) => {
      const result = this.showResult(show);
      const cell = this.qs(`#showVenue${i}`);
      if (!cell) return;
      cell.innerHTML = `${result.minApplied ? '<span class="badge text-bg-warning fw-normal me-1" title="Mínimo de la sala aplicado">mín.</span>' : ""}${this.eur(result.venue)}`;
      cell.title = `Taquilla ${this.eur(result.gross)} · artista ${this.eur(result.artist)}`;
      if (show.include) {
        total += result.venue;
        showsTotal += result.venue;
        showsTickets += show.tickets || 0;
        lines.push([show.name, `Parte sala${result.minApplied ? " (mínimo aplicado)" : ""}`, result.venue]);
      } else {
        lines.push([show.name, "Excluido del cálculo", result.venue, true]);
      }
    });
    this.qs("#showsFoot").innerHTML = `<tr><td colspan="2" class="text-end">Total</td><td class="text-end">${showsTickets}</td><td colspan="6"></td><td class="text-end">${this.eur(showsTotal)}</td><td></td></tr>`;

    let concertsTotal = 0;
    let concertsTickets = 0;
    this.state.concerts.forEach((concert, i) => {
      const result = this.concertResult(concert);
      const cell = this.qs(`#concertResult${i}`);
      if (!cell) return;
      cell.title = `Taquilla ${this.eur((concert.price || 0) * (concert.tickets || 0))}`;
      if (!result.excluded) {
        cell.innerHTML = `<span class="${result.value < 0 ? "neg" : ""}">${this.eur(result.value)}</span>`;
        total += result.value;
        concertsTotal += result.value;
        concertsTickets += concert.tickets || 0;
        lines.push([concert.name, `${concert.tickets} entradas`, result.value]);
      } else {
        cell.textContent = this.eur(0);
        lines.push([concert.name, "Excluido del cálculo", 0, true]);
      }
    });
    this.qs("#concertsFoot").innerHTML = `<tr><td colspan="2" class="text-end">Total</td><td class="text-end">${concertsTickets}</td><td colspan="4"></td><td class="text-end ${concertsTotal < 0 ? "neg" : ""}">${this.eur(concertsTotal)}</td><td></td></tr>`;

    let staffTotal = 0;
    let realTotal = 0;
    this.state.staff.forEach((emp, i) => {
      const cost = this.staffCost(emp);
      staffTotal += cost.total;
      realTotal += cost.realHours;
      const jornadaInfo = this.qs(`#jornadaInfo${i}`);
      if (!jornadaInfo) return;
      jornadaInfo.textContent = !emp.jornada.length
        ? "coste 0,00 €"
        : cost.paysJornada
          ? `${this.hrs(cost.jornadaHours)} · ${this.eur(cost.jornadaPay)}`
          : `${this.hrs(cost.jornadaHours)} contratadas`;
      this.qs(`#realInfo${i}`).textContent = emp.coming ? `${this.hrs(cost.realHours)} · ${this.hrs(cost.nightHours)} nocturnas` : "No viene";
      const chip = (label, value, cls = "") => `<span class="chip ${value === 0 ? "zero" : ""} ${cls}">${label} ${this.eur(value)}</span>`;
      this.qs(`#costChips${i}`).innerHTML = this.costChips(emp, cost, chip);
      this.qs(`#costTotal${i}`).textContent = this.eur(cost.total);
    });
    this.qs("#staffFoot").innerHTML = `<tr class="fw-bold"><td colspan="3" class="text-end">Total</td><td>${this.hrs(realTotal)} reales</td><td class="text-end">${this.eur(staffTotal)}</td></tr>`;
    const staffCharged = this.charged(staffTotal);
    total -= staffCharged;
    lines.push(["Personal", `${this.state.staff.length} empleados · ${this.hrs(realTotal)} reales · 40 % de cargas`, -staffCharged]);

    this.qs("#resultTotal").innerHTML = `<span class="${total < 0 ? "neg" : ""}">${this.eur(total)}</span>`;
    this.qs("#resultWarning").innerHTML = warning;
    this.qs("#resultLines").innerHTML = lines.map(([what, sub, amount, excluded]) => `
      <div class="result-line ${excluded ? "excluded" : ""}"><div class="what">${this.esc(what)}<small>${this.esc(sub)}</small></div>
      <div class="amt ${amount != null && amount < 0 && !excluded ? "neg" : ""}">${amount == null ? "—" : (excluded ? this.eur(amount) : this.signedEur(amount))}</div></div>`).join("");
  }

  leave(event) {
    if (!this.dirty) return;
    if (!window.confirm("Hay cambios sin guardar. ¿Salir sin guardar?")) event.preventDefault();
  }

  async save(event) {
    event.preventDefault();
    const response = await fetch(this.saveUrlValue, {
      method: this.saveMethodValue || "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ simulation: { payload: this.persistedPayload() } })
    });
    if (!response.ok) {
      const badge = this.qs("#dirtyBadge");
      if (badge) {
        badge.hidden = false;
        badge.textContent = "No se ha podido guardar";
      }
      return;
    }
    const body = await response.json();
    window.location = body.redirect || this.listUrlValue;
  }

  setDirty(value) {
    this.dirty = value;
    const badge = this.qs("#dirtyBadge");
    if (!badge) return;
    badge.hidden = !value;
    if (value) badge.textContent = "Cambios sin guardar";
  }

  persistedPayload() {
    const payload = this.clone(this.state);
    delete payload.summary;
    payload.summary = this.buildSummary();
    return payload;
  }

  // Figures of the list. Same rules as the result column, with the 40 % employer
  // charge included in the staff cost. Empty shows or concerts stay null so the
  // list prints a dash instead of zero.
  buildSummary() {
    const bar = this.state.bar || {};
    const barValue = bar.revenue != null && bar.marginPct != null ? bar.revenue * bar.marginPct / 100 : null;
    const barLines = barValue == null
      ? [bar.revenue == null ? "Faltan ingresos y margen" : `${this.eur(bar.revenue)} × margen sin indicar`]
      : [`${this.eur(bar.revenue)} × ${bar.marginPct} %`];

    let showsTotal = 0;
    const shows = this.state.shows || [];
    const showLines = shows.map((show) => {
      const result = this.showResult(show);
      if (show.include) showsTotal += result.venue;
      return `${show.name} · ${show.tickets || 0} entradas${show.include ? ` · sala ${this.eur(result.venue)}` : " · excluido"}`;
    });

    let concertsTotal = 0;
    const concerts = this.state.concerts || [];
    const concertLines = concerts.map((concert) => {
      const result = this.concertResult(concert);
      if (!result.excluded) concertsTotal += result.value;
      return `${concert.name} · ${concert.tickets || 0} entradas · coste fijo ${this.eur(concert.fixedCost || 0)}${concert.include ? "" : " · excluido"}`;
    });

    let staffTotal = 0;
    const staffLines = (this.state.staff || []).map((emp) => {
      const cost = this.staffCost(emp);
      staffTotal += cost.total;
      const hours = emp.coming
        ? (emp.real || []).filter((slot) => slot[0] && slot[1]).map((slot) => `${slot[0]}–${slot[1]}`).join(" + ") || "sin horario"
        : "no viene";
      return `${String(emp.name || "").split(" ")[0]} ${hours} · ${this.eur(this.charged(cost.total))}`;
    });
    const staffCharged = this.charged(staffTotal);

    return {
      staff: -staffCharged,
      bar: barValue,
      shows: shows.length ? showsTotal : null,
      concerts: concerts.length ? concertsTotal : null,
      total: (barValue || 0) + showsTotal + concertsTotal - staffCharged,
      incomplete: barValue == null,
      staffLines,
      barLines,
      showLines,
      concertLines
    };
  }

  charged(amount) {
    return Math.round((Number(amount) || 0) * 1.4 * 100) / 100;
  }

  costChips(emp, cost, chip) {
    if (!emp.coming) {
      return cost.paysJornada ? chip("Jornada (no viene)", cost.jornadaPay, "base") : chip("No viene", 0, "base");
    }
    if (cost.onHoliday) {
      return [chip("Jornada", cost.jornadaPay, "base"), chip(`+ Horas al ${this.state.params.holidayPct} %`, cost.holiday), chip("+ Nocturno", cost.night)].join(" ");
    }
    if (cost.onVacation) {
      return [chip("Jornada (vacaciones)", cost.jornadaPay, "base"), chip("+ Nocturno", cost.night), chip(`+ Exceso${cost.excessHours ? ` (${this.hrs(cost.excessHours)})` : ""}`, cost.excess)].join(" ");
    }
    return [chip("Horas", cost.worked, "base"), chip("+ Nocturno", cost.night)].join(" ");
  }

  // Normal day: only the hours worked. Vacation: the jornada, plus any hours
  // beyond it. Festivo: the jornada plus the hours worked at holidayPct
  // (140 by default), whether or not those hours fill the jornada.
  staffCost(emp) {
    const jornadaHours = this.hoursOf(emp.jornada);
    const rate = emp.rate;
    const onHoliday = !!emp.onHoliday;
    const onVacation = !!emp.vacation && !onHoliday;
    const paysJornada = onHoliday || !!emp.vacation;
    const jornadaPay = paysJornada ? jornadaHours * rate : 0;
    const blank = { jornadaHours, realHours: 0, nightHours: 0, excessHours: 0, jornadaPay, worked: 0, holiday: 0, night: 0, excess: 0, paysJornada, onHoliday, onVacation, total: jornadaPay };
    if (!emp.coming) return blank;

    const realHours = this.hoursOf(emp.real);
    const nightHours = this.nightHoursOf(emp.real);
    const night = nightHours * rate * this.state.params.nightPct / 100;
    if (onHoliday) {
      const holiday = realHours * rate * this.state.params.holidayPct / 100;
      return { ...blank, realHours, nightHours, jornadaPay: jornadaHours * rate, holiday, night, paysJornada: true, total: jornadaHours * rate + holiday + night };
    }
    if (onVacation) {
      const excessHours = Math.max(0, realHours - jornadaHours);
      const excess = excessHours * rate;
      return { ...blank, realHours, nightHours, excessHours, jornadaPay: jornadaHours * rate, night, excess, paysJornada: true, total: jornadaHours * rate + excess + night };
    }
    const worked = realHours * rate;
    return { ...blank, realHours, nightHours, worked, night, paysJornada: false, total: worked + night };
  }

  showResult(show) {
    const gross = (show.tickets || 0) * (show.price || 0);
    const value = show.value || 0;
    const amount = show.shareType === "pct" ? gross * value / 100 : show.shareType === "per_ticket" ? value * (show.tickets || 0) : value;
    const venueBeforeMin = show.recipient === "venue" ? amount : gross - amount;
    const venue = Math.max(venueBeforeMin, show.venueMin || 0);
    return { gross, venue, artist: gross - venue, minApplied: venue > venueBeforeMin };
  }

  concertResult(concert) {
    if (!concert.include) return { value: 0, excluded: true };
    return {
      value: (concert.price || 0) * (concert.tickets || 0) - (concert.fixedCost || 0) - (concert.variableCost || 0) * (concert.tickets || 0),
      excluded: false
    };
  }

  toggleComing(index, on) {
    const emp = this.state.staff[index];
    emp.coming = on;
    emp.real = on ? this.clone(emp.jornada.length ? emp.jornada : [["", ""]]) : [];
    this.renderStaff();
    this.compute();
  }

  toggleInclude(list, index, on) {
    this.state[list][index].include = on;
    if (list === "shows") this.renderShows();
    else this.renderConcerts();
    this.compute();
  }

  addSlot(index) {
    this.state.staff[index].real.push(["", ""]);
    this.renderStaff();
    this.compute();
  }

  removeSlot(index, slot) {
    this.state.staff[index].real.splice(slot, 1);
    this.renderStaff();
    this.compute();
  }

  addEmployee() {
    const select = this.qs("#addEmployeeSelect");
    const person = (this.state.pool || []).find((item) => item.id === Number(select.value));
    if (!person || this.state.staff.some((emp) => emp.id === person.id)) return;
    this.state.staff.push({
      id: person.id, name: person.name, rate: person.rate, jornada: [], coming: true, real: [["", ""]],
      added: true, vacation: false, onHoliday: !!this.state.holiday
    });
    this.renderStaff();
    this.compute();
  }

  removeEmployee(index) {
    this.state.staff.splice(index, 1);
    this.renderStaff();
    this.compute();
  }

  addShow() {
    this.state.shows.push({
      name: "Espectáculo (ficticio)", time: "21:00", tickets: this.defaultTickets("21:00", false), price: 12, value: 30,
      shareType: "pct", recipient: "venue", venueMin: 0, include: true, source: "Añadido manualmente"
    });
    this.renderShows();
    this.compute();
  }

  addConcert() {
    this.state.concerts.push({
      name: "Concierto (ficticio)", time: "20:30", tickets: this.defaultTickets("20:30", true), price: 10,
      fixedCost: 0, variableCost: 5.5, include: true, source: "Añadido manualmente"
    });
    this.renderConcerts();
    this.compute();
  }

  removeRow(list, index) {
    this.state[list].splice(index, 1);
    if (list === "shows") this.renderShows();
    else this.renderConcerts();
    this.compute();
  }

  hoursOf(slots) {
    return slots.reduce((sum, slot) => {
      const minutes = this.slotMinutes(slot);
      return minutes ? sum + (minutes[1] - minutes[0]) / 60 : sum;
    }, 0);
  }

  nightHoursOf(slots) {
    const windows = [[0, 360], [1320, 1800]];
    return slots.reduce((sum, slot) => {
      const minutes = this.slotMinutes(slot);
      if (!minutes) return sum;
      return sum + windows.reduce((total, [start, end]) => total + Math.max(0, Math.min(end, minutes[1]) - Math.max(start, minutes[0])) / 60, 0);
    }, 0);
  }

  slotMinutes([start, end]) {
    if (!start || !end) return null;
    let from = this.toMin(start);
    let to = this.toMin(end);
    if (to <= from) to += 1440;
    return [from, to];
  }

  toMin(value) {
    const [hours, minutes] = value.split(":").map(Number);
    return hours * 60 + minutes;
  }

  slotLabel(slot) {
    const overnight = slot[0] && slot[1] && this.toMin(slot[1]) <= this.toMin(slot[0]);
    return `${this.esc(slot[0])}–${this.esc(slot[1])}${overnight ? '<span class="plus1">+1</span>' : ""}`;
  }

  get(path) {
    return path.split(".").reduce((object, key) => (object == null ? object : object[key]), this.state);
  }

  set(path, value) {
    const keys = path.split(".");
    const last = keys.pop();
    const object = keys.reduce((current, key) => current[key], this.state);
    object[last] = value;
  }

  num(key, value, step = 1) {
    return `<input type="number" step="${step}" class="form-control form-control-sm num" value="${value ?? ""}" data-k="${key}">`;
  }

  txt(key, value, type = "text") {
    return `<input type="${type}" class="form-control form-control-sm" value="${this.esc(value ?? "")}" data-k="${key}">`;
  }

  sel(key, value, options) {
    return `<select class="form-select form-select-sm" data-k="${key}">${options.map(([option, label]) => `<option value="${option}" ${value === option ? "selected" : ""}>${label}</option>`).join("")}</select>`;
  }

  includeSwitch(list, index, on) {
    return `<div class="form-check form-switch d-inline-block m-0"><input class="form-check-input" type="checkbox" ${on ? "checked" : ""} data-include="${list}" data-index="${index}"></div>`;
  }

  nameCell(key, value, source) {
    return `${this.txt(key, value)}${source ? `<div class="src mt-1">${this.esc(source)}</div>` : ""}`;
  }

  eur(value) {
    const amount = Number(value) || 0;
    return `${amount < 0 ? "−" : ""}${Math.abs(amount).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  signedEur(value) {
    return `${value > 0 ? "+" : ""}${this.eur(value)}`;
  }

  hrs(value) {
    return `${Number(value).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
  }

  num1(value) {
    return Number(value).toLocaleString("es-ES", { maximumFractionDigits: 1 });
  }

  esc(value) {
    return String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[char]));
  }

  clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  qs(selector) {
    return this.element.querySelector(selector);
  }
}
